import {
  getChannelId,
  getMessageId,
  getMessageTimestamp,
  getReactionChatUserId,
  getReactionEmoji,
  getReactionId,
} from 'app/shared/components/TeamChat/TeamChat.utils';

import { TeamChatActionType, TeamChatConnectionStatus } from 'constants/teamChat';

import { RESET_STATE } from 'store/global/actions';

const initialState = {
  channels: {},
  messages: {},
  cursors: {},
  hasMore: {},
  readReceipts: {},
  typingUsers: {},
  onlineUsers: {},
  pinnedMessageIds: {},
  userMentions: [],
  isLoadingUserMentions: false,
  activeChannelId: null,
  isLoadingChannels: false,
  isLoadingMessages: {},
  isSending: {},
  connectionStatus: TeamChatConnectionStatus.DISCONNECTED,
  error: null,
  pendingEntityAttachment: null,
  pendingEntityChatFilter: null,
};

const addIdToList = (list = [], id) => (list.includes(id) ? list : [...list, id]);

const removeIdFromList = (list = [], id) => {
  const next = list.filter(existing => existing !== id);

  return next.length === list.length ? list : next;
};

const indexChannelsById = channels =>
  channels.reduce((acc, channel) => {
    const channelId = getChannelId(channel);

    if (!channelId) {
      return acc;
    }

    acc[channelId] = channel;

    return acc;
  }, {});

const getMessageSortKey = message => {
  const timestamp = getMessageTimestamp(message);

  if (!timestamp) {
    return Number.MAX_SAFE_INTEGER;
  }

  const parsed = new Date(timestamp).getTime();

  return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
};

const sortMessagesByCreated = messages => {
  if (messages.length < 2) {
    return messages;
  }

  return [...messages].sort((a, b) => getMessageSortKey(a) - getMessageSortKey(b));
};

const mergeMessages = (existing = [], incoming = [], isInitial) => {
  const merged = isInitial ? incoming : [...incoming, ...existing];

  return sortMessagesByCreated(merged);
};

const applyReactionChange = (messages = [], messageId, transform) => {
  if (!messageId) {
    return messages;
  }

  const index = messages.findIndex(msg => getMessageId(msg) === messageId);

  if (index === -1) {
    return messages;
  }

  const existing = messages[index];
  const currentReactions = existing.reactions ?? existing.Reactions ?? [];
  const nextReactions = transform(currentReactions);

  if (nextReactions === currentReactions) {
    return messages;
  }

  const next = [...messages];
  next[index] = { ...existing, reactions: nextReactions };

  return next;
};

const addReactionToList = (reactions, reaction) => {
  if (!reaction) {
    return reactions;
  }

  const reactionId = getReactionId(reaction);
  const emoji = getReactionEmoji(reaction);
  const chatUserId = getReactionChatUserId(reaction);

  const alreadyPresent = reactions.some(existing => {
    const existingId = getReactionId(existing);

    if (reactionId && existingId) {
      return existingId === reactionId;
    }

    return getReactionEmoji(existing) === emoji && getReactionChatUserId(existing) === chatUserId;
  });

  if (alreadyPresent) {
    return reactions;
  }

  return [...reactions, reaction];
};

const removeReactionFromList = (reactions, { chatUserId, emoji }) => {
  const filtered = reactions.filter(
    existing =>
      !(getReactionEmoji(existing) === emoji && getReactionChatUserId(existing) === chatUserId),
  );

  return filtered.length === reactions.length ? reactions : filtered;
};

const upsertMessage = (existing = [], message) => {
  const messageId = getMessageId(message);

  if (!messageId) {
    return existing;
  }

  const index = existing.findIndex(msg => getMessageId(msg) === messageId);

  if (index === -1) {
    return sortMessagesByCreated([...existing, message]);
  }

  const next = [...existing];
  next[index] = { ...next[index], ...message };

  return sortMessagesByCreated(next);
};

const teamChatReducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case TeamChatActionType.FETCH_CHANNELS:
      return { ...state, isLoadingChannels: true, error: null };

    case TeamChatActionType.FETCH_CHANNELS_SUCCESS: {
      const indexed = indexChannelsById(payload.channels);

      return {
        ...state,
        isLoadingChannels: false,
        channels: { ...state.channels, ...indexed },
      };
    }

    case TeamChatActionType.FETCH_CHANNELS_FAILURE:
      return { ...state, isLoadingChannels: false, error: payload };

    case TeamChatActionType.FETCH_CHANNEL_SUCCESS: {
      if (!payload?.chatId || !payload?.channel) {
        return state;
      }

      return {
        ...state,
        channels: {
          ...state.channels,
          [payload.chatId]: {
            ...(state.channels[payload.chatId] ?? {}),
            ...payload.channel,
          },
        },
      };
    }

    case TeamChatActionType.FETCH_MESSAGES:
      return {
        ...state,
        isLoadingMessages: { ...state.isLoadingMessages, [payload.chatId]: true },
      };

    case TeamChatActionType.FETCH_MESSAGES_SUCCESS:
      return {
        ...state,
        isLoadingMessages: { ...state.isLoadingMessages, [payload.chatId]: false },
        messages: {
          ...state.messages,
          [payload.chatId]: mergeMessages(
            state.messages[payload.chatId],
            payload.messages,
            payload.isInitial,
          ),
        },
        cursors: { ...state.cursors, [payload.chatId]: payload.cursor },
        hasMore: { ...state.hasMore, [payload.chatId]: payload.hasMore },
      };

    case TeamChatActionType.FETCH_MESSAGES_FAILURE:
      return {
        ...state,
        isLoadingMessages: { ...state.isLoadingMessages, [payload.chatId]: false },
        error: payload.error,
      };

    case TeamChatActionType.SEND_MESSAGE:
      return {
        ...state,
        isSending: { ...state.isSending, [payload.chatId]: true },
      };

    case TeamChatActionType.SEND_MESSAGE_SUCCESS:
      return {
        ...state,
        isSending: { ...state.isSending, [payload.chatId]: false },
        messages: {
          ...state.messages,
          [payload.chatId]: upsertMessage(state.messages[payload.chatId], payload.message),
        },
      };

    case TeamChatActionType.SEND_MESSAGE_FAILURE:
      return {
        ...state,
        isSending: { ...state.isSending, [payload.chatId]: false },
        error: payload.error,
      };

    case TeamChatActionType.UPLOAD_ATTACHMENT:
      return {
        ...state,
        isSending: { ...state.isSending, [payload.chatId]: true },
      };

    case TeamChatActionType.UPLOAD_ATTACHMENT_SUCCESS:
      return {
        ...state,
        isSending: { ...state.isSending, [payload.chatId]: false },
        messages: {
          ...state.messages,
          [payload.chatId]: upsertMessage(state.messages[payload.chatId], payload.message),
        },
      };

    case TeamChatActionType.UPLOAD_ATTACHMENT_FAILURE:
      return {
        ...state,
        isSending: { ...state.isSending, [payload.chatId]: false },
        error: payload.error,
      };

    case TeamChatActionType.UPDATE_MESSAGE_SUCCESS:
      return {
        ...state,
        messages: {
          ...state.messages,
          [payload.chatId]: upsertMessage(state.messages[payload.chatId], payload.message),
        },
      };

    case TeamChatActionType.UPDATE_MESSAGE_FAILURE:
      return { ...state, error: payload.error };

    case TeamChatActionType.MESSAGE_UPDATED: {
      if (!payload?.chatId || !payload?.messageId) {
        return state;
      }

      const chatMessages = state.messages[payload.chatId] ?? [];
      const index = chatMessages.findIndex(msg => getMessageId(msg) === payload.messageId);

      if (index === -1) {
        return state;
      }

      const existing = chatMessages[index];
      const nextMessage = {
        ...existing,
        payload: payload.messagePayload,
        updated: payload.updated,
      };
      const nextMessages = [...chatMessages];
      nextMessages[index] = nextMessage;

      return {
        ...state,
        messages: { ...state.messages, [payload.chatId]: nextMessages },
      };
    }

    case TeamChatActionType.MESSAGE_RECEIVED: {
      const existingChannel = state.channels[payload.chatId] ?? {};
      const isActiveChannel = state.activeChannelId === payload.chatId;
      const currentUnread = existingChannel.unreadMessageCount ?? 0;
      const incomingTimestamp = getMessageTimestamp(payload.message);

      return {
        ...state,
        messages: {
          ...state.messages,
          [payload.chatId]: upsertMessage(state.messages[payload.chatId], payload.message),
        },
        channels: {
          ...state.channels,
          [payload.chatId]: {
            ...existingChannel,
            lastMessage: payload.message,
            lastMessageAt: incomingTimestamp ?? existingChannel.lastMessageAt ?? null,
            unreadMessageCount: isActiveChannel ? currentUnread : currentUnread + 1,
          },
        },
      };
    }

    case TeamChatActionType.MARK_AS_READ: {
      const channel = state.channels[payload.chatId] ?? {};

      return {
        ...state,
        channels: {
          ...state.channels,
          [payload.chatId]: { ...channel, unreadMessageCount: 0 },
        },
      };
    }

    case TeamChatActionType.MESSAGES_READ: {
      if (!payload?.chatId || !payload?.chatUserId) {
        return state;
      }

      const chatReceipts = state.readReceipts[payload.chatId] ?? {};

      return {
        ...state,
        readReceipts: {
          ...state.readReceipts,
          [payload.chatId]: {
            ...chatReceipts,
            [payload.chatUserId]: payload.lastReadAt,
          },
        },
      };
    }

    case TeamChatActionType.TYPING_STARTED: {
      if (!payload?.chatId || !payload?.chatUserId) {
        return state;
      }

      const chatTyping = state.typingUsers[payload.chatId] ?? {};

      return {
        ...state,
        typingUsers: {
          ...state.typingUsers,
          [payload.chatId]: {
            ...chatTyping,
            [payload.chatUserId]: { memberId: payload.memberId, at: Date.now() },
          },
        },
      };
    }

    case TeamChatActionType.TYPING_STOPPED: {
      if (!payload?.chatId || !payload?.chatUserId) {
        return state;
      }

      const chatTyping = state.typingUsers[payload.chatId];

      if (!chatTyping || !(payload.chatUserId in chatTyping)) {
        return state;
      }

      const { [payload.chatUserId]: _removed, ...remaining } = chatTyping;

      return {
        ...state,
        typingUsers: {
          ...state.typingUsers,
          [payload.chatId]: remaining,
        },
      };
    }

    case TeamChatActionType.REACTION_ADDED: {
      if (!payload?.chatId || !payload?.messageId || !payload?.reaction) {
        return state;
      }

      const chatMessages = state.messages[payload.chatId] ?? [];
      const nextMessages = applyReactionChange(chatMessages, payload.messageId, reactions =>
        addReactionToList(reactions, payload.reaction),
      );

      if (nextMessages === chatMessages) {
        return state;
      }

      return {
        ...state,
        messages: { ...state.messages, [payload.chatId]: nextMessages },
      };
    }

    case TeamChatActionType.ADD_REACTION_SUCCESS: {
      if (!payload?.chatId || !payload?.messageId || !payload?.reaction) {
        return state;
      }

      const chatMessages = state.messages[payload.chatId] ?? [];
      const nextMessages = applyReactionChange(chatMessages, payload.messageId, reactions =>
        addReactionToList(reactions, payload.reaction),
      );

      if (nextMessages === chatMessages) {
        return state;
      }

      return {
        ...state,
        messages: { ...state.messages, [payload.chatId]: nextMessages },
      };
    }

    case TeamChatActionType.REACTION_REMOVED: {
      if (!payload?.chatId || !payload?.messageId || !payload?.emoji || !payload?.chatUserId) {
        return state;
      }

      const chatMessages = state.messages[payload.chatId] ?? [];
      const nextMessages = applyReactionChange(chatMessages, payload.messageId, reactions =>
        removeReactionFromList(reactions, {
          chatUserId: payload.chatUserId,
          emoji: payload.emoji,
        }),
      );

      if (nextMessages === chatMessages) {
        return state;
      }

      return {
        ...state,
        messages: { ...state.messages, [payload.chatId]: nextMessages },
      };
    }

    case TeamChatActionType.FETCH_PRESENCE_SUCCESS: {
      if (!payload?.chatId) {
        return state;
      }

      return {
        ...state,
        onlineUsers: {
          ...state.onlineUsers,
          [payload.chatId]: payload.chatUserIds ?? [],
        },
      };
    }

    case TeamChatActionType.USER_ONLINE: {
      if (!payload?.chatId || !payload?.chatUserId) {
        return state;
      }

      const current = state.onlineUsers[payload.chatId] ?? [];

      if (current.includes(payload.chatUserId)) {
        return state;
      }

      return {
        ...state,
        onlineUsers: {
          ...state.onlineUsers,
          [payload.chatId]: [...current, payload.chatUserId],
        },
      };
    }

    case TeamChatActionType.USER_OFFLINE: {
      if (!payload?.chatId || !payload?.chatUserId) {
        return state;
      }

      const current = state.onlineUsers[payload.chatId] ?? [];
      const next = current.filter(id => id !== payload.chatUserId);

      if (next.length === current.length) {
        return state;
      }

      return {
        ...state,
        onlineUsers: {
          ...state.onlineUsers,
          [payload.chatId]: next,
        },
      };
    }

    case TeamChatActionType.FETCH_PINNED_MESSAGES_SUCCESS: {
      if (!payload?.chatId) {
        return state;
      }

      const incoming = payload.messages ?? [];
      const ids = incoming.map(message => getMessageId(message)).filter(Boolean);
      const mergedMessages = incoming.reduce(
        (acc, message) => upsertMessage(acc, message),
        state.messages[payload.chatId] ?? [],
      );

      return {
        ...state,
        messages: { ...state.messages, [payload.chatId]: mergedMessages },
        pinnedMessageIds: { ...state.pinnedMessageIds, [payload.chatId]: ids },
      };
    }

    case TeamChatActionType.PIN_MESSAGE_SUCCESS:

    case TeamChatActionType.MESSAGE_PINNED: {
      if (!payload?.chatId || !payload?.messageId) {
        return state;
      }

      const current = state.pinnedMessageIds[payload.chatId] ?? [];
      const nextList = addIdToList(current, payload.messageId);

      if (nextList === current) {
        return state;
      }

      return {
        ...state,
        pinnedMessageIds: { ...state.pinnedMessageIds, [payload.chatId]: nextList },
      };
    }

    case TeamChatActionType.UNPIN_MESSAGE_SUCCESS:

    case TeamChatActionType.MESSAGE_UNPINNED: {
      if (!payload?.chatId || !payload?.messageId) {
        return state;
      }

      const current = state.pinnedMessageIds[payload.chatId] ?? [];
      const nextList = removeIdFromList(current, payload.messageId);

      if (nextList === current) {
        return state;
      }

      return {
        ...state,
        pinnedMessageIds: { ...state.pinnedMessageIds, [payload.chatId]: nextList },
      };
    }

    case TeamChatActionType.PIN_CHAT_SUCCESS: {
      if (!payload?.chatId) {
        return state;
      }

      const channel = state.channels[payload.chatId];

      if (!channel) {
        return state;
      }

      return {
        ...state,
        channels: {
          ...state.channels,
          [payload.chatId]: { ...channel, pinnedAt: payload.pinnedAt ?? new Date().toISOString() },
        },
      };
    }

    case TeamChatActionType.UNPIN_CHAT_SUCCESS: {
      if (!payload?.chatId) {
        return state;
      }

      const channel = state.channels[payload.chatId];

      if (!channel) {
        return state;
      }

      return {
        ...state,
        channels: {
          ...state.channels,
          [payload.chatId]: { ...channel, pinnedAt: null },
        },
      };
    }

    case TeamChatActionType.FETCH_USER_MENTIONS:
      return { ...state, isLoadingUserMentions: true };

    case TeamChatActionType.FETCH_USER_MENTIONS_SUCCESS:
      return {
        ...state,
        isLoadingUserMentions: false,
        userMentions: payload?.mentions ?? [],
      };

    case TeamChatActionType.FETCH_USER_MENTIONS_FAILURE:
      return { ...state, isLoadingUserMentions: false };

    case TeamChatActionType.REQUEST_ENTITY_CHAT_ATTACHMENT:
      return { ...state, pendingEntityAttachment: payload };

    case TeamChatActionType.CLEAR_ENTITY_CHAT_ATTACHMENT:
      return { ...state, pendingEntityAttachment: null };

    case TeamChatActionType.REQUEST_ENTITY_CHAT_FILTER:
      return { ...state, pendingEntityChatFilter: payload };

    case TeamChatActionType.CLEAR_ENTITY_CHAT_FILTER:
      return { ...state, pendingEntityChatFilter: null };

    case TeamChatActionType.SET_ACTIVE_CHANNEL:
      return { ...state, activeChannelId: payload };

    case TeamChatActionType.SET_CONNECTION_STATUS:
      return { ...state, connectionStatus: payload };

    case TeamChatActionType.RESET_STATE:
    case RESET_STATE:
      return initialState;

    default:
      return state;
  }
};

export default teamChatReducer;
